import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import useNoIndex from "@/lib/useNoIndex";
import { Loader2, CheckCircle2, AlertCircle, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { TimePicker } from "@/components/TimePicker";
import { API } from "@/lib/api";

// Public, no-auth lead form for the AI Bridal Manager.
// Reachable at /request-quote/{user_slug}.
//
// Visual brief (luxury bridal atelier):
//   • Background: warm beige   #F8F5F1
//   • Cards     : white         #FFFFFF
//   • Primary   : champagne gold #C8A97E
//   • Hover     : darker gold   #B8956A
//   • Text      : warm dark grey #4A4A4A
//   • Borders   : soft          #EAEAEA
//
// Language resolution (the lead is a new contact → no client.language yet):
//   1. URL query ?lang=it|en overrides everything.
//   2. navigator.language (it-IT → IT, else EN).
//   3. User toggle in the top-right corner.
// Strict 2-bucket rule consistent with the rest of the app:
//   "it" → Italian, anything else → English.

// ---------- i18n content ----------
const COPY = {
  it: {
    badge: "Atelier",
    heading_line1: "Il Suo matrimonio merita",
    heading_line2: "qualcosa di davvero indimenticabile",
    subtitle: "Un'esperienza beauty pensata esclusivamente per Lei",
    intro: "Ci aiuti a conoscerLa meglio",
    section_contacts: "I Suoi contatti",
    section_wedding: "Il Suo matrimonio",
    section_notes: "Desideri e ispirazioni",
    f_name: "Nome e cognome",
    f_email: "Email",
    f_phone: "Telefono",
    f_date: "Data del matrimonio",
    f_time: "Orario della cerimonia",
    f_prep: "Luogo di preparazione",
    f_prep_ph: "Es. hotel, casa della sposa…",
    f_venue: "Location della cerimonia",
    f_venue_ph: "Es. chiesa, comune, resort…",
    f_people: "Quante persone desidera includere nella preparazione?",
    f_people_ph: "Es. 1 (sposa) · 3 (sposa + 2 mamme)",
    f_notes: "Ci racconti qualcosa di più: stile, ispirazioni, desideri…",
    f_notes_ph:
      "Ci racconti il Suo sogno: stile, ispirazioni, atmosfera o qualsiasi idea per il Suo matrimonio…",
    required: "Campo obbligatorio",
    error_fill: "La preghiamo di completare i campi obbligatori (nome, email, telefono, data del matrimonio).",
    submit: "Inizi il Suo percorso",
    submitting: "Invio in corso…",
    success_title: "La Sua richiesta è partita",
    success_body:
      "Grazie per aver condiviso con noi i dettagli del Suo matrimonio. Stiamo già lavorando per creare una proposta pensata esclusivamente per Lei. Riceverà il Suo preventivo entro 48–72 ore.",
    signature_role: "Bridal Hair & Makeup Specialist",
    signature_fallback: "Your Bridal Specialist",
    powered: "Powered by",
    invalid_title: "Link non valido",
    invalid_body: "Il link condiviso non risulta valido. La invitiamo a contattare lo studio per riceverne uno aggiornato.",
    lang_toggle_to: "EN",
    time_within: "entro 48–72 ore",
  },
  en: {
    badge: "Atelier",
    heading_line1: "Your wedding deserves",
    heading_line2: "something truly unforgettable",
    subtitle: "A beauty experience designed exclusively for you",
    intro: "Help us get to know you better",
    section_contacts: "Your contact details",
    section_wedding: "Your wedding",
    section_notes: "Your vision",
    f_name: "Full name",
    f_email: "Email",
    f_phone: "Phone",
    f_date: "Wedding date",
    f_time: "Ceremony time",
    f_prep: "Preparation location",
    f_prep_ph: "E.g. hotel, bride's home…",
    f_venue: "Ceremony location",
    f_venue_ph: "E.g. church, city hall, resort…",
    f_people: "How many people would you like to include in the preparation?",
    f_people_ph: "E.g. 1 (bride) · 3 (bride + 2 mothers)",
    f_notes: "Tell us more about your vision, style and inspirations…",
    f_notes_ph:
      "Tell us about your dream: style, inspirations, atmosphere or anything you imagine for your wedding day…",
    required: "Required",
    error_fill: "Please complete the required fields (name, email, phone, wedding date).",
    submit: "Start your journey",
    submitting: "Submitting…",
    success_title: "Your request has been sent",
    success_body:
      "Thank you for sharing your wedding details with us. We are already working on a proposal designed exclusively for you. You will receive your quote within 48–72 hours.",
    signature_role: "Bridal Hair & Makeup Specialist",
    signature_fallback: "Your Bridal Specialist",
    powered: "Powered by",
    invalid_title: "Invalid link",
    invalid_body: "The shared link is not valid. Please contact the studio to receive an updated one.",
    lang_toggle_to: "IT",
    time_within: "within 48–72 hours",
  },
};

// Detect the preferred language once on mount, with URL override.
// Strict 2-bucket: "it" → it, everything else → en.
function detectInitialLang() {
  try {
    const q = new URLSearchParams(window.location.search).get("lang");
    if (q) return q.toLowerCase() === "it" ? "it" : "en";
    const nav = (navigator.language || "").toLowerCase();
    return nav.startsWith("it") ? "it" : "en";
  } catch {
    return "en";
  }
}

export default function RequestQuote() {
  useNoIndex();
  const { slug } = useParams();
  const [studio, setStudio] = useState(null); // null=loading, false=not found, {studio_name}
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [lang, setLang] = useState(detectInitialLang);
  const t = useMemo(() => COPY[lang], [lang]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    wedding_date: "",
    ceremony_time: "",
    prep_venue: "",
    venue: "",
    people_count: "",
    notes: "",
  });

  useEffect(() => {
    fetch(`${API}/public/lead-info/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setStudio(d))
      .catch(() => setStudio(false));
  }, [slug]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.wedding_date) {
      setError(t.error_fill);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        people_count: parseInt(form.people_count, 10) || 1,
        // Propagate the chosen language to the backend so the created client
        // record inherits it — keeps downstream emails/PDF in the same language.
        language: lang,
      };
      const r = await fetch(`${API}/public/lead/${slug}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || t.error_fill);
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- shared wrapper styles (palette inlined to avoid Tailwind config edits) ----------
  const pageBg = { backgroundColor: "#F8F5F1", color: "#4A4A4A" };
  const cardStyle = {
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    boxShadow: "0 10px 40px -18px rgba(74,74,74,0.18), 0 2px 8px -4px rgba(74,74,74,0.05)",
    border: "1px solid #EAEAEA",
  };
  const inputStyle = {
    height: "48px",
    borderRadius: "8px",
    borderColor: "#EAEAEA",
    backgroundColor: "#FFFFFF",
    color: "#4A4A4A",
    fontSize: "15px",
  };
  const textareaStyle = {
    ...inputStyle,
    height: "auto",
    minHeight: "120px",
    lineHeight: "1.7",
    padding: "14px 16px",
  };
  const labelCls =
    "block text-[12px] tracking-[0.16em] uppercase font-medium mb-2";
  const labelStyle = { color: "#8A8278", letterSpacing: "0.14em" };

  // ---------- LOADING ----------
  if (studio === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={pageBg}>
        <Loader2 className="w-8 h-8 animate-spin" strokeWidth={1.2} style={{ color: "#C8A97E" }} />
      </div>
    );
  }

  // ---------- INVALID SLUG ----------
  if (studio === false) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={pageBg}>
        <div style={cardStyle} className="p-10 max-w-md w-full text-center" data-testid="lead-not-found">
          <AlertCircle className="w-9 h-9 mx-auto mb-5" strokeWidth={1.2} style={{ color: "#C8A97E" }} />
          <h1 className="font-serif-heading text-3xl" style={{ color: "#2C2C2C" }}>
            {t.invalid_title}
          </h1>
          <p className="text-sm mt-3 leading-relaxed" style={{ color: "#6E6E6E" }}>
            {t.invalid_body}
          </p>
        </div>
      </div>
    );
  }

  // ---------- SUCCESS ----------
  if (done) {
    const proName = (studio.professional_name || "").trim();
    // Prefer the operator's custom brand_role if set; falls back to the
    // canonical "Bridal Hair & Makeup Specialist" tagline.
    const sigRole = (studio.brand_role || "").trim() || t.signature_role;
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={pageBg} data-testid="lead-success-page">
        <div style={cardStyle} className="p-10 sm:p-12 max-w-xl w-full text-center">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: "#F5EFE6" }}
          >
            <CheckCircle2 className="w-8 h-8" strokeWidth={1.2} style={{ color: "#C8A97E" }} />
          </div>
          <p className="text-[11px] tracking-[0.24em] uppercase mb-3" style={{ color: "#C8A97E" }}>
            {studio.studio_name}
          </p>
          <h1 className="font-serif-heading text-4xl leading-tight" style={{ color: "#2C2C2C" }}>
            {t.success_title}
          </h1>
          <p className="text-[15px] mt-6 leading-[1.85]" style={{ color: "#4A4A4A" }}>
            {t.success_body}
          </p>

          {/* Human-touch signature — the lead sees the professional's first name
              instead of a faceless brand. Falls back gracefully when the profile
              name is missing. */}
          <div
            className="mt-10 pt-6 border-t"
            style={{ borderColor: "#EAEAEA" }}
            data-testid="lead-signature"
          >
            {proName ? (
              <>
                <p
                  className="font-serif-heading italic text-2xl"
                  style={{ color: "#2C2C2C" }}
                  data-testid="lead-signature-name"
                >
                  {proName}
                </p>
                <p
                  className="text-[11px] tracking-[0.22em] uppercase mt-2"
                  style={{ color: "#C8A97E" }}
                >
                  {sigRole}
                </p>
              </>
            ) : (
              <p
                className="font-serif-heading italic text-xl"
                style={{ color: "#C8A97E" }}
                data-testid="lead-signature-fallback"
              >
                {t.signature_fallback}
              </p>
            )}
          </div>

          <div className="mt-8 pt-5 border-t" style={{ borderColor: "#EAEAEA" }}>
            <p className="text-[11px] tracking-[0.18em] uppercase" style={{ color: "#B5AFA5" }}>
              {t.powered}{" "}
              <span className="font-serif-heading normal-case tracking-normal">AI Bridal Manager™</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ---------- FORM ----------
  const toggleLang = () => setLang((l) => (l === "it" ? "en" : "it"));

  return (
    <div className="min-h-screen" style={pageBg} data-testid="lead-form-page">
      {/* Language toggle — fixed top right, non-intrusive */}
      <button
        type="button"
        onClick={toggleLang}
        className="fixed top-5 right-5 z-10 rounded-full px-4 py-2 text-[11px] tracking-[0.18em] uppercase font-medium transition-all"
        style={{
          backgroundColor: "#FFFFFF",
          color: "#8A8278",
          border: "1px solid #EAEAEA",
          boxShadow: "0 2px 8px -4px rgba(74,74,74,0.08)",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = "#C8A97E"; e.currentTarget.style.borderColor = "#C8A97E"; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = "#8A8278"; e.currentTarget.style.borderColor = "#EAEAEA"; }}
        data-testid="lead-lang-toggle"
        aria-label={`Switch language to ${t.lang_toggle_to}`}
      >
        {t.lang_toggle_to}
      </button>

      <div className="max-w-[680px] mx-auto py-14 sm:py-20 px-5">
        {/* Studio header — logo (if uploaded) replaces the wordmark.
            Falls back to brand_name in tracked uppercase. */}
        <div className="text-center mb-8">
          {studio.logo_url ? (
            <img
              src={studio.logo_url}
              alt={studio.brand_name || studio.studio_name}
              className="mx-auto max-h-[60px] max-w-[200px] object-contain mb-2"
              data-testid="lead-studio-logo"
            />
          ) : (
            <p
              className="text-[11px] tracking-[0.28em] uppercase mb-2"
              style={{ color: "#C8A97E" }}
              data-testid="lead-studio-name"
            >
              {studio.studio_name}
            </p>
          )}
          {studio.brand_role && (
            <p
              className="text-[10px] tracking-[0.22em] uppercase mt-1"
              style={{ color: "#8A8278" }}
              data-testid="lead-studio-role"
            >
              {studio.brand_role}
            </p>
          )}
          <div
            className="inline-block w-10 h-px my-3"
            style={{ backgroundColor: "#C8A97E", opacity: 0.6 }}
            aria-hidden
          />
        </div>

        {/* Heading */}
        <div className="text-center mb-10">
          <h1
            className="font-serif-heading text-4xl sm:text-5xl leading-[1.15]"
            style={{ color: "#2C2C2C", fontWeight: 400 }}
          >
            {t.heading_line1}
            <br />
            <span style={{ color: "#C8A97E", fontStyle: "italic" }}>{t.heading_line2}</span>
          </h1>
          {/* Subtitle — italic champagne, conveys the "exclusive" promise */}
          <p
            className="font-serif-heading italic mt-5 text-lg sm:text-xl tracking-wide"
            style={{ color: "#C8A97E" }}
            data-testid="lead-subtitle"
          >
            {t.subtitle}
          </p>
        </div>

        {/* Soft emotional intro — sits above the form card, whisper-small */}
        <p
          className="text-center text-[13px] tracking-[0.18em] uppercase mb-5"
          style={{ color: "#8A8278" }}
          data-testid="lead-intro"
        >
          {t.intro}
        </p>

        {/* Form card */}
        <form onSubmit={onSubmit} style={cardStyle} className="p-7 sm:p-10 space-y-10">
          {/* Section: Contacts */}
          <section>
            <h2
              className="font-serif-heading text-xl mb-6 pb-3 border-b"
              style={{ color: "#2C2C2C", borderColor: "#EAEAEA" }}
            >
              {t.section_contacts}
            </h2>
            <div className="space-y-5">
              <div>
                <Label htmlFor="lead-name" className={labelCls} style={labelStyle}>
                  {t.f_name} *
                </Label>
                <Input
                  id="lead-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  required
                  style={inputStyle}
                  className="focus-visible:ring-2 focus-visible:ring-offset-0"
                  data-testid="lead-name-input"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="lead-email" className={labelCls} style={labelStyle}>
                    {t.f_email} *
                  </Label>
                  <Input
                    id="lead-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    required
                    style={inputStyle}
                    data-testid="lead-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="lead-phone" className={labelCls} style={labelStyle}>
                    {t.f_phone} *
                  </Label>
                  <Input
                    id="lead-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    required
                    style={inputStyle}
                    data-testid="lead-phone-input"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Section: Wedding */}
          <section>
            <h2
              className="font-serif-heading text-xl mb-6 pb-3 border-b"
              style={{ color: "#2C2C2C", borderColor: "#EAEAEA" }}
            >
              {t.section_wedding}
            </h2>
            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="lead-date" className={labelCls} style={labelStyle}>
                    {t.f_date} *
                  </Label>
                  <Input
                    id="lead-date"
                    type="date"
                    value={form.wedding_date}
                    onChange={(e) => update("wedding_date", e.target.value)}
                    required
                    style={inputStyle}
                    data-testid="lead-date-input"
                  />
                </div>
                <div>
                  <Label className={labelCls} style={labelStyle}>
                    {t.f_time}
                  </Label>
                  <TimePicker
                    value={form.ceremony_time}
                    onChange={(v) => update("ceremony_time", v || "")}
                    testid="lead-time-input"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="lead-prep" className={labelCls} style={labelStyle}>
                    {t.f_prep}
                  </Label>
                  <Input
                    id="lead-prep"
                    value={form.prep_venue}
                    onChange={(e) => update("prep_venue", e.target.value)}
                    placeholder={t.f_prep_ph}
                    style={inputStyle}
                    data-testid="lead-prep-input"
                  />
                </div>
                <div>
                  <Label htmlFor="lead-venue" className={labelCls} style={labelStyle}>
                    {t.f_venue}
                  </Label>
                  <Input
                    id="lead-venue"
                    value={form.venue}
                    onChange={(e) => update("venue", e.target.value)}
                    placeholder={t.f_venue_ph}
                    style={inputStyle}
                    data-testid="lead-venue-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="lead-people" className={labelCls} style={labelStyle}>
                  {t.f_people}
                </Label>
                <Input
                  id="lead-people"
                  type="number"
                  min={1}
                  value={form.people_count}
                  onChange={(e) => update("people_count", e.target.value)}
                  placeholder={t.f_people_ph}
                  style={{ ...inputStyle, maxWidth: "320px" }}
                  data-testid="lead-people-input"
                />
              </div>
            </div>
          </section>

          {/* Section: Notes */}
          <section>
            <h2
              className="font-serif-heading text-xl mb-6 pb-3 border-b"
              style={{ color: "#2C2C2C", borderColor: "#EAEAEA" }}
            >
              {t.section_notes}
            </h2>
            <Label htmlFor="lead-notes" className={labelCls} style={labelStyle}>
              {t.f_notes}
            </Label>
            <Textarea
              id="lead-notes"
              rows={5}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder={t.f_notes_ph}
              style={textareaStyle}
              data-testid="lead-notes-input"
            />
          </section>

          {error && (
            <div
              className="rounded-md p-4 text-sm"
              style={{
                backgroundColor: "#FBF4EC",
                border: "1px solid #E8D9C4",
                color: "#8A5A2B",
              }}
              data-testid="lead-error"
            >
              {error}
            </div>
          )}

          {/* Submit */}
          <div className="pt-2">
            <Button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto sm:min-w-[260px] h-[52px] px-10 text-[15px] font-medium tracking-wide rounded-lg transition-all"
              style={{
                backgroundColor: submitting ? "#D9C9B2" : "#C8A97E",
                color: "#FFFFFF",
                border: "none",
                boxShadow: "0 4px 14px -4px rgba(200,169,126,0.5)",
                letterSpacing: "0.02em",
              }}
              onMouseEnter={(e) => {
                if (!submitting) e.currentTarget.style.backgroundColor = "#B8956A";
              }}
              onMouseLeave={(e) => {
                if (!submitting) e.currentTarget.style.backgroundColor = "#C8A97E";
              }}
              data-testid="lead-submit-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2.5 animate-spin" strokeWidth={1.5} />
                  {t.submitting}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2.5" strokeWidth={1.5} />
                  {t.submit}
                </>
              )}
            </Button>
            <p
              className="mt-4 text-[12px] text-center sm:text-left"
              style={{ color: "#B5AFA5" }}
            >
              {t.time_within}
            </p>
          </div>
        </form>

        {/* Discreet professional signature under the form — builds trust before submit */}
        {studio.professional_name ? (
          <div className="text-center mt-10" data-testid="lead-form-signature">
            <div
              className="inline-block w-8 h-px mb-3"
              style={{ backgroundColor: "#C8A97E", opacity: 0.5 }}
              aria-hidden
            />
            <p
              className="font-serif-heading italic text-lg"
              style={{ color: "#2C2C2C" }}
            >
              {studio.professional_name}
            </p>
            <p
              className="text-[11px] tracking-[0.22em] uppercase mt-1"
              style={{ color: "#C8A97E" }}
            >
              {(studio.brand_role || "").trim() || t.signature_role}
            </p>
          </div>
        ) : null}

        {/* Footer */}
        <p
          className="text-center text-[11px] tracking-[0.22em] uppercase mt-12"
          style={{ color: "#B5AFA5" }}
        >
          {t.powered}{" "}
          <span className="font-serif-heading normal-case tracking-normal" style={{ color: "#8A8278" }}>
            AI Bridal Manager™
          </span>
        </p>
      </div>
    </div>
  );
}
